import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { newsList } from '@/mocks/news';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function NewsDetail() {
  const { id = '' } = useParams();
  const article = newsList.find((n) => n.id === id);

  usePageMeta(
    article ? `${article.title} - 红桃影视资讯` : '资讯详情 - 红桃影视',
    article ? article.summary : '阅读影视行业资讯与专题解读。',
  );

  if (!article) {
    return (
      <div className="px-4 md:px-6 lg:px-10 py-24 flex flex-col items-center text-center">
        <span className="w-20 h-20 rounded-full bg-background-200 flex items-center justify-center text-4xl text-foreground-500">
          <i className="ri-article-line" />
        </span>
        <h1 className="mt-6 text-2xl font-bold text-foreground-950">资讯不存在或已删除</h1>
        <p className="mt-3 text-foreground-600">该资讯可能已被移除，去看看其他内容吧。</p>
        <Link
          to="/news"
          className="inline-flex mt-6 px-6 h-11 items-center rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = newsList
    .filter((n) => n.id !== article.id && n.category === article.category)
    .slice(0, 3);
  const fallback = newsList.filter((n) => n.id !== article.id).slice(0, 3);
  const recommendations = related.length > 0 ? related : fallback;

  return (
    <div className="pb-6">
      <div className="border-b border-background-300/60 bg-background-100">
        <div className="px-4 md:px-6 lg:px-10 py-5">
          <Breadcrumb items={[{ label: '影视资讯', to: '/news' }, { label: '正文' }]} />
        </div>
      </div>

      <article className="px-4 md:px-6 lg:px-10 mt-8 max-w-4xl mx-auto">
        <span className="inline-block px-3 py-1 rounded-full bg-accent-500/20 text-accent-400 text-xs font-medium">
          {article.category}
        </span>
        <h1 className="mt-4 text-2xl md:text-3xl font-bold text-foreground-950 font-heading leading-snug">
          {article.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-300/60">
          <span>
            <i className="ri-user-line mr-1" />
            {article.author}
          </span>
          <span>
            <i className="ri-calendar-line mr-1" />
            发布于 {article.date}
          </span>
          <span>
            <i className="ri-eye-line mr-1" />
            阅读 {1200 + article.id.length * 137}
          </span>
        </div>

        <div className="mt-6 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} 配图`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="mt-8 space-y-5">
          {article.content.map((para) => (
            <p key={para.slice(0, 12)} className="text-base text-foreground-700 leading-loose">
              {para}
            </p>
          ))}
        </div>

        <div className="mt-8 p-4 rounded-lg bg-secondary-100/50 border border-background-300/60 text-sm text-foreground-600">
          <i className="ri-information-line text-accent-400 mr-2" />
          声明：本页资讯内容为产品演示所用的示范文案，不代表真实新闻报道。
        </div>
      </article>

      <section className="px-4 md:px-6 lg:px-10 mt-14 max-w-4xl mx-auto">
        <h2 className="text-xl font-bold text-foreground-950 font-heading flex items-center gap-3 mb-6">
          <span className="w-1.5 h-6 rounded-full bg-primary-500" />
          相关文章
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {recommendations.map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.id}`}
              className="group rounded-lg overflow-hidden bg-background-100 border border-background-300/60 hover:border-primary-500/50 transition-colors"
            >
              <span className="block w-full aspect-[16/9] overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} 资讯`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </span>
              <span className="block p-4">
                <span className="block text-sm font-medium text-foreground-900 group-hover:text-primary-400 transition-colors line-clamp-2">
                  {item.title}
                </span>
                <span className="block mt-2 text-xs text-foreground-500">{item.date}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}